import template from './login.html';
import azureIcon from './images/icon-azure.png';
import googleIcon from './images/icon-google.png';
import oktaIcon from './images/icon-okta.png';
import './login.scss';
/**
 * This controller is in charge of validating the user credentials and handling the selection of a
 * SSO provider
 */
export class LoginController {
  /**
   * @param {$scope} $scope To access the form as an `ngForm`.
   */
  constructor($scope) {
    'ngInject';

    /**
     * A local reference for the component scope.
     * @type {$scope}
     */
    this.$scope = $scope;
    /**
     * A dictionary for the form bindings.
     * @type {Object}
     * @property {string} email    The user email address.
     * @property {string} password The user password.
     */
    this.credentials = {
      email: '',
      password: '',
    };

    /**
     * A dictionary with the icons and the button labels for the supported SSO providers.
     * @type {Object}
     */
    this.providersInfo = {
      gmail: {
        icon: googleIcon,
        label: 'Log in with Google',
      },
      okta: {
        icon: oktaIcon,
        label: 'Log in with OKTA',
      },
      azuread: {
        icon: azureIcon,
        label: 'Log in with Azure AD',
      },
    };
    /**
     * The error message generated when the form is being validated.
     * For the sake of only having one property with the error message, when the `error` binding
     * changes, its value gets also copied to this property. The alternative would be to overwrite
     * the binding value, but that doesn't sound right.
     * @type {string}
     */
    this.errorMessage = '';
  }
  /**
   * - When the `error` binding changes, copy its value to the `errorMessage` property.
   * - When the `initialEmail` changes, set it as the `credentials.email` model value.
   * @param {Object}  changes              A dictionary with the bindings that were updated.
   * @param {?Object} changes.error        A dictionary with the changes for the `error` binding.
   * @param {?Object} changes.initialEmail A dictionary with the changes for the `initialEmail`
   *                                       binding.
   */
  $onChanges({ error, initialEmail }) {
    if (error && error.currentValue) {
      this.errorMessage = error.currentValue;
    }

    if (initialEmail && !initialEmail.previousValue && initialEmail.currentValue) {
      this.credentials.email = initialEmail.currentValue;
    }
  }
  /**
   * Invokes the callback that informs that the user wants to change the email address, so the
   * login options should be resetted.
   */
  changeEmailAddress() {
    this.onSaveEmailAddress({
      email: '',
    });
  }
  /**
   * This is called when the form is submitted. If this is the first time it happens, it calls
   * the method that validates and obtains the login options (credentials or SSO) for the user
   * email address; but if this is called after the user alrady set the email address, it means that
   * he/she is trying to login using credentials, so it calls the method that handles that.
   */
  login() {
    if (!this.loading) {
      if (this.emailReady) {
        this._loginWithCredentials();
      } else {
        this._completeFirstStep();
      }
    }
  }
  /**
   * Invokes the callback that informs that a SSO provider was selected.
   * @param {SSOProvider} provider The selected provider information.
   */
  selectProvider(provider) {
    this.onLoginWithProvider({ provider });
  }
  /**
   * Gets the form instance.
   *
   * @returns {ngForm}
   */
  get form() {
    return this.$scope.ssoAuthBox_form;
  }
  /**
   * This is called the first time the user enters his/her email address. It validates the address
   * and invokes the callback to load the login options.
   * @access protected
   * @ignore
   */
  _completeFirstStep() {
    const { form } = this;
    if (form.$valid) {
      this.errorMessage = '';
      this.onSaveEmailAddress({
        email: this.credentials.email,
      });
    } else if (form.$error.email) {
      this.errorMessage = 'Please enter a valid e-mail address';
    } else {
      this.errorMessage = 'Please complete the form';
    }
  }
  /**
   * This is called when the user is trying to login using credentials (email and password). It
   * validates that there's a password (because the email was already set if this method was
   * called) and invokes the callback to do the login.
   * @access protected
   * @ignore
   */
  _loginWithCredentials() {
    const { form } = this;
    if (form.$valid) {
      this.errorMessage = '';
      this.onLoginWithCredentials(this.credentials);
    } else {
      this.errorMessage = 'Please complete the form';
    }
  }
}
/**
 * Shows the SSO login form where the user has to enter an email address in order to see if he/she
 * can login using credentials (password) or SSO (by selecting a provider).
 * @ngesdoc component
 * @ngname login
 * @ngcontroller {@link SSOLoginController}
 * @ngmodule auth
 * @ngbinding {string}   initialEmail           An email address to set on the email field as
 *                                              initial value. This can be used when the address
 *                                              is saved and restored.
 * @ngbinding {string}   subtitle               A subtitle to place under the Olapic logo. Usually
 *                                              the name of the app the user is about tolog to.
 * @ngbinding {string}   error                  An error message to show.
 * @ngbinding {boolean}  loading                Whether or not the component should show a loading
 *                                              indicator.
 * @ngbinding {boolean}  emailReady             Whether or not the email address was already saved.
 * @ngbinding {boolean}  credentialsEnabled     Whether or not the user can login using email and
 *                                              password.
 * @ngbinding {string}   forgotPasswordUrl      The URL for the `Forgot your password?` link.
 * @ngbinding {string}   privacyPolicyUrl       The URL for the `Privacy policy` link.
 * @ngbinding {string}   requestDemoUrl         The URL for the `Request a demo` link.
 * @ngbinding {Array}    providers              The list of SSO providers avaiable for the user
 *                                              email address.
 * @ngbinding {Function} onSaveEmailAddress     The callback the component calls when the user
 *                                              enters his/her email address. It receives the
 *                                              `email` as parameter. It can also be used to reset
 *                                              the login options by sending an empty string.
 * @ngbinding {Function} onLoginWithCredentials The callback the component calls when the user
 *                                              wants to login using his/her email address and
 *                                              password. It receives `email` and `password` as
 *                                              parameters.
 * @ngbinding {Function} onLoginWithProvider    The callback the component calls when the user
 *                                              selects an SSO provider. It recevies a `provider`
 *                                              parameter.
 */
export default {
  controller: LoginController,
  template,
  bindings: {
    initialEmail: '<',
    subtitle: '<',
    error: '<',
    loading: '<',
    emailReady: '<',
    credentialsEnabled: '<',
    forgotPasswordUrl: '<',
    privacyPolicyUrl: '<',
    requestDemoUrl: '<',
    providers: '<',
    onSaveEmailAddress: '&',
    onLoginWithCredentials: '&',
    onLoginWithProvider: '&',
  },
};
